import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  VStack,
  Badge,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { CheckCircle, Eye, EyeOff, ExternalLink, KeyRound, Search, Shield } from "lucide-react";
import type { Candidate } from "../../types/onboarding.ts";
import { useApiRequest } from "../../hooks/useApiRequest.ts";
import { settings } from "../../shared/settings.ts";
import { UserAvatar } from "../../components/UserAvatar";
import { ErrorState } from "../../components/ErrorState";
import { ListPagination } from "../rh/onboarding/components/ListPagination";
import { OnflyLoading } from "../../tools/OnflyLoading";

const BASE_URL = settings.environment.VITE_STARTER_PACK_BASE_URL;
// Máximo aceito pelo back — usado pra buscar todos os candidatos em poucas
// páginas (a busca/paginação de exibição roda sobre a lista completa, então
// precisamos ter todo mundo carregado antes de filtrar/paginar no front).
const FETCH_PAGE_SIZE = 200;
const DISPLAY_PAGE_SIZE = 20;

const CyberUploadPage = () => {
  const [allCandidates, setAllCandidates] = useState<Candidate[]>([]);
  const [filtered, setFiltered]           = useState<Candidate[]>([]);
  const [fetchError, setFetchError]       = useState(false);
  const [isFetching, setIsFetching]       = useState(false);
  const [search, setSearch]               = useState("");
  const [page, setPage]                   = useState(1);

  const [modalEmail, setModalEmail]       = useState<string | null>(null);
  const [login, setLogin]                 = useState("");
  const [password, setPassword]           = useState("");
  const [showPassword, setShowPassword]   = useState(false);
  const [isSubmitting, setIsSubmitting]   = useState(false);

  const { request: fetchReq } = useApiRequest();
  const { request: saveReq } = useApiRequest();
  const toast = useToast();

  const bg           = useColorModeValue("white", "gray.800");
  const borderColor  = useColorModeValue("gray.100", "whiteAlpha.200");
  const hoverBg      = useColorModeValue("gray.50", "whiteAlpha.100");
  const labelColor   = useColorModeValue("gray.700", "whiteAlpha.900");
  const subtitleColor = useColorModeValue("gray.500", "whiteAlpha.600");
  const cardBorder   = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.1)");
  const inputBg      = useColorModeValue("white", "rgba(255,255,255,0.05)");

  const fetchCandidates = useCallback(async () => {
    setFetchError(false);
    setIsFetching(true);
    try {
      // O endpoint pagina no back (page_size máx. 200) — buscamos todas as
      // páginas pra ter TODOS os candidatos disponíveis aqui, sem nenhum
      // filtro de elegibilidade (ex.: ticket Atlassian), pra ninguém ficar de
      // fora e o Cyber conseguir informar credenciais pra qualquer candidato.
      let all: Candidate[] = [];
      let currentPage = 1;
      let totalPages = 1;
      do {
        const data: any = await fetchReq({
          url: `${BASE_URL}/api/v1/admin/onboarding/candidates`,
          method: "GET",
          params: { page: String(currentPage), page_size: String(FETCH_PAGE_SIZE) },
          skipToast: true,
        });
        all = all.concat(data?.candidates ?? []);
        totalPages = data?.total_pages ?? 1;
        currentPage += 1;
      } while (currentPage <= totalPages);

      setAllCandidates(all);
    } catch {
      setFetchError(true);
    } finally {
      setIsFetching(false);
    }
  }, [fetchReq]);

  useEffect(() => { fetchCandidates(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const term = search.toLowerCase();
    setFiltered(
      term
        ? allCandidates.filter(
            c => c.name.toLowerCase().includes(term) || c.email.toLowerCase().includes(term),
          )
        : allCandidates,
    );
  }, [search, allCandidates]);

  useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / DISPLAY_PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * DISPLAY_PAGE_SIZE, page * DISPLAY_PAGE_SIZE);

  const openModal = (email: string) => {
    setModalEmail(email);
    setLogin("");
    setPassword("");
    setShowPassword(false);
  };

  const closeModal = () => {
    setModalEmail(null);
  };

  const handleSubmit = async () => {
    if (!modalEmail || !login.trim() || !password.trim()) return;
    setIsSubmitting(true);
    try {
      await saveReq({
        url: `${BASE_URL}/api/v1/admin/candidates/${encodeURIComponent(modalEmail)}/cyber/credentials`,
        method: "POST",
        payload: { login: login.trim(), password: password.trim() },
        skipToast: true,
      });
      toast({ title: "Credenciais salvas com sucesso!", status: "success", duration: 3000, isClosable: true });
      setAllCandidates(prev =>
        prev.map(c => c.email === modalEmail ? { ...c, has_cyber_credentials: true } : c),
      );
      closeModal();
    } catch {
      toast({ title: "Erro ao salvar credenciais", status: "error", duration: 4000, isClosable: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedCandidate = allCandidates.find(c => c.email === modalEmail) ?? null;

  return (
    <Box maxW="900px" mx="auto" py={8} px={4}>
      {/* Header */}
      <HStack spacing={3} mb={2}>
        <Flex
          align="center" justify="center"
          boxSize="44px" borderRadius="lg"
          bg={useColorModeValue("purple.50", "rgba(128,90,213,0.2)")}
          flexShrink={0}
        >
          <Icon as={Shield} boxSize={5} color={useColorModeValue("purple.600", "purple.300")} />
        </Flex>
        <Box>
          <Text fontSize="xl" fontWeight="bold" color={labelColor}>Credenciais de Acesso Cyber</Text>
          <Text fontSize="sm" color={subtitleColor}>
            Todos os candidatos, disponíveis para informar as credenciais de acesso.
          </Text>
        </Box>
      </HStack>

      {/* Search */}
      <InputGroup mt={6} mb={4}>
        <InputLeftElement pointerEvents="none">
          <Icon as={Search} boxSize={4} color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder="Buscar por nome ou e-mail..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          bg={bg}
        />
      </InputGroup>

      {/* List */}
      <Box borderWidth="1px" borderColor={borderColor} borderRadius="xl" overflow="hidden" bg={bg}>
        {isFetching ? (
          <Flex justify="center" py={12}>
            <OnflyLoading containerSize={80} />
          </Flex>
        ) : fetchError ? (
          <ErrorState onRetry={fetchCandidates} />
        ) : filtered.length === 0 ? (
          <Flex direction="column" align="center" py={12} gap={2}>
            <Icon as={Shield} boxSize={8} color="gray.300" />
            <Text fontSize="sm" color={subtitleColor}>
              {search ? "Nenhum candidato encontrado." : "Nenhum candidato disponível."}
            </Text>
          </Flex>
        ) : (
          <VStack spacing={0} align="stretch" divider={<Box borderBottomWidth="1px" borderColor={borderColor} />}>
            {paginated.map(candidate => (
              <Flex
                key={candidate.email}
                align="center"
                gap={4}
                px={5}
                py={4}
                _hover={{ bg: hoverBg }}
                transition="background 0.15s"
              >
                <UserAvatar
                  size="sm"
                  name={candidate.name}
                  email={candidate.email}
                  flexShrink={0}
                />

                <Box flex={1} minW={0}>
                  <Text fontSize="sm" fontWeight="semibold" color={labelColor} noOfLines={1}>
                    {candidate.name}
                  </Text>
                  <HStack spacing={2} mt={0.5}>
                    <Text fontSize="xs" color={subtitleColor} noOfLines={1}>
                      {candidate.email}
                    </Text>
                    {candidate.atlassian_ticket_id && (
                      <Badge
                        colorScheme="blue"
                        variant="subtle"
                        fontSize="2xs"
                        display="inline-flex"
                        alignItems="center"
                        gap={1}
                        flexShrink={0}
                      >
                        <Icon as={ExternalLink} boxSize={2.5} />
                        {candidate.atlassian_ticket_id}
                      </Badge>
                    )}
                  </HStack>
                </Box>

                <HStack spacing={3} flexShrink={0}>
                  {candidate.has_cyber_credentials ? (
                    <Badge
                      colorScheme="green"
                      variant="subtle"
                      display="inline-flex"
                      alignItems="center"
                      gap={1}
                      px={2}
                    >
                      <Icon as={CheckCircle} boxSize={3} />
                      Credenciais enviadas
                    </Badge>
                  ) : (
                    <Badge colorScheme="gray" variant="subtle">Sem credenciais</Badge>
                  )}

                  <Tooltip
                    label={candidate.has_cyber_credentials ? "Atualizar credenciais" : "Informar credenciais"}
                    fontSize="xs"
                  >
                    <Button
                      size="sm"
                      colorScheme="purple"
                      variant={candidate.has_cyber_credentials ? "ghost" : "solid"}
                      leftIcon={<Icon as={KeyRound} boxSize={3.5} />}
                      onClick={() => openModal(candidate.email)}
                    >
                      {candidate.has_cyber_credentials ? "Atualizar" : "Informar"}
                    </Button>
                  </Tooltip>
                </HStack>
              </Flex>
            ))}
          </VStack>
        )}
      </Box>

      <ListPagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {/* Modal de credenciais */}
      <Modal isOpen={!!modalEmail} onClose={closeModal} size="sm">
        <ModalOverlay />
        <ModalContent borderRadius="2xl">
          <ModalHeader
            fontSize="md"
            fontWeight={600}
            color={labelColor}
            borderBottomWidth="1px"
            borderColor={cardBorder}
            pb={4}
          >
            Credenciais de Primeiro Acesso
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pt={5}>
            {selectedCandidate && (
              <Box mb={4} p={3} borderRadius="8px" bg={useColorModeValue("purple.50", "rgba(128,90,213,0.08)")} borderWidth="1px" borderColor="rgba(128,90,213,0.2)">
                <Text fontSize="xs" color={subtitleColor}>
                  Candidato: <Text as="span" fontWeight={600} color={labelColor}>{selectedCandidate.name}</Text>
                </Text>
              </Box>
            )}
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={1}>Login</FormLabel>
                <Input
                  value={login}
                  onChange={e => setLogin(e.target.value)}
                  placeholder="ex: joao.silva"
                  bg={inputBg}
                  borderColor={cardBorder}
                  borderRadius="8px"
                  fontSize="sm"
                  _focus={{ borderColor: "purple.400", boxShadow: "0 0 0 2px rgba(128,90,213,0.15)" }}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={1}>Primeira Senha</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    bg={inputBg}
                    borderColor={cardBorder}
                    borderRadius="8px"
                    fontSize="sm"
                    _focus={{ borderColor: "purple.400", boxShadow: "0 0 0 2px rgba(128,90,213,0.15)" }}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                      icon={<Icon as={showPassword ? EyeOff : Eye} boxSize={4} />}
                      size="xs"
                      variant="ghost"
                      color="gray.400"
                      _hover={{ color: "purple.500" }}
                      onClick={() => setShowPassword(p => !p)}
                    />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter borderTopWidth="1px" borderColor={cardBorder} gap={2} pt={4}>
            <Button variant="ghost" borderRadius="50px" size="sm" onClick={closeModal}>Cancelar</Button>
            <Button
              colorScheme="purple"
              borderRadius="50px"
              size="sm"
              isLoading={isSubmitting}
              isDisabled={!login.trim() || !password.trim()}
              onClick={handleSubmit}
            >
              Salvar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CyberUploadPage;
